import { User } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "./cn";

export interface UserAvatarProps extends ComponentProps<"span"> {
  readonly name?: string | null;
  readonly email?: string | null;
  readonly image?: string | null;
  readonly size?: "xs" | "sm" | "md" | "lg";
}

const sizeClasses = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-7 w-7 text-xs",
  md: "h-8 w-8 text-xs",
  lg: "h-9 w-9 text-sm",
} as const;

const iconSizes = {
  xs: 12,
  sm: 14,
  md: 15,
  lg: 18,
} as const;

function computeInitials(name?: string | null, email?: string | null): string | null {
  if (name && name.trim().length > 0) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0];
    const last = parts[parts.length - 1];
    if (parts.length >= 2 && first && last && first.length > 0 && last.length > 0) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }
    return name.trim().slice(0, 2).toUpperCase();
  }
  if (email && email.trim().length > 0) {
    return email.trim().slice(0, 2).toUpperCase();
  }
  return null;
}

export function UserAvatar({
  name,
  email,
  image,
  size = "md",
  className,
  ...props
}: UserAvatarProps) {
  const initials = computeInitials(name, email);

  return (
    <span
      className={cn(
        "relative inline-grid shrink-0 select-none place-items-center overflow-hidden rounded-full bg-[var(--hover-strong)] font-semibold text-[var(--muted)] ring-1 ring-[var(--border)]",
        sizeClasses[size],
        className,
      )}
      aria-hidden="true"
      {...props}
    >
      {image ? (
        <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />
      ) : initials ? (
        <span>{initials}</span>
      ) : (
        <User size={iconSizes[size]} strokeWidth={2} className="opacity-85" />
      )}
    </span>
  );
}
